<template>
  <div>
    <v-container fluid>

      <!-- ========== OVERLAY ========== -->
      <v-overlay :value="overlay" opacity="0">
        <v-progress-circular color="primary" indeterminate size="50"></v-progress-circular>
      </v-overlay>

      <!-- ========== HEADER + FILTERS ========== -->
      <v-row>
        <v-col cols="12">
          <v-card class="rounded-lg shadow">
            <v-card-title class="d-flex align-center">
              <div>
                <div class="fs-18 font-weight-bold">Opération QR Code · Dashboard</div>
                <div class="fs-13 text--secondary">
                  Analyse POS &amp; Consommateurs · {{ allUsages.length | toCurrency }} paquets vendus chargés
                  <span class="ml-1">· depuis le {{ operationStartLabel }}</span>
                  <span v-if="excludedBeforeStart" class="ml-1">
                    ({{ excludedBeforeStart | toCurrency }} écarté(s) hors période)
                  </span>
                </div>
              </div>
              <v-spacer/>
              <v-btn color="primary" depressed small class="text-none mr-2" @click="fetchUsages">
                <v-icon left small>mdi-refresh</v-icon>
                Actualiser
              </v-btn>
            </v-card-title>
            <v-divider/>
            <v-card-text>
              <v-row dense align="center">
                <v-col cols="12" sm="6" md="4" lg="2">
                  <v-select
                      v-model="filters.region"
                      :items="regionOptions"
                      label="Région"
                      prepend-inner-icon="mdi-earth"
                      clearable
                      hide-details
                      outlined
                      dense
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4" lg="3">
                  <v-select
                      v-model="filters.wilaya"
                      :items="filteredWilayaOptions"
                      label="Wilaya"
                      prepend-inner-icon="mdi-map-marker"
                      clearable
                      hide-details
                      outlined
                      dense
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4" lg="2">
                  <v-select
                      v-model="filters.segment"
                      :items="segmentOptions"
                      label="Segment"
                      prepend-inner-icon="mdi-tag"
                      clearable
                      hide-details
                      outlined
                      dense
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4" lg="2">
                  <v-select
                      v-model="filters.offre"
                      :items="offreOptions"
                      label="Offre"
                      prepend-inner-icon="mdi-gift-outline"
                      clearable
                      hide-details
                      outlined
                      dense
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4" lg="2">
                  <v-select
                      v-model="filters.period"
                      :items="periodOptions"
                      label="Période"
                      prepend-inner-icon="mdi-calendar"
                      hide-details
                      outlined
                      dense
                  />
                </v-col>
                <v-col cols="12" md="4" lg="1" class="text-right">
                  <v-btn
                      color="grey darken-1"
                      text
                      small
                      class="text-none"
                      @click="resetFilters"
                      :disabled="!hasActiveFilters"
                  >
                    <v-icon left small>mdi-filter-off</v-icon>
                    Réinit.
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- ========== KPI CARDS ========== -->
      <v-row v-if="isLoading">
        <v-col cols="12" lg="3" v-for="item in 4" :key="item">
          <v-card class="rounded-lg shadow">
            <v-card-text>
              <v-skeleton-loader type="article"></v-skeleton-loader>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-else>

        <!-- Paquets vendus -->
        <v-col cols="12" lg="3">
          <v-card class="rounded-lg shadow">
            <v-card-title class="pb-0 text-no-wrap">
              <span class="fs-15">Paquets vendus</span>
              <v-spacer/>
              <v-avatar color="indigo" size="38">
                <v-icon dark>mdi-package-variant</v-icon>
              </v-avatar>
            </v-card-title>
            <v-card-text>
              <h3 class="mb-2 indigo--text">
                {{ kpis.totalScans | toCurrency }}
                <small class="indigo--text text--lighten-1 fs-13">paquets</small>
              </h3>
              <div class="d-flex flex-wrap" style="gap: 4px;">
                <v-chip x-small label color="blue" dark>
                  O1: {{ kpis.packsByOffre['OFFRE 1'] | toCurrency }}
                </v-chip>
                <v-chip x-small label color="deep-purple" dark>
                  O2: {{ kpis.packsByOffre['OFFRE 2'] | toCurrency }}
                </v-chip>
                <v-chip x-small label color="teal" dark>
                  O3: {{ kpis.packsByOffre['OFFRE 3'] | toCurrency }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Taux réalisation -->
        <v-col cols="12" lg="3">
          <v-card class="rounded-lg shadow">
            <v-card-title class="pb-0 text-no-wrap">
              <span class="fs-15">Taux de réalisation</span>
              <v-spacer/>
              <v-avatar color="blue" size="38">
                <v-icon dark>mdi-gauge</v-icon>
              </v-avatar>
            </v-card-title>
            <v-card-text>
              <h3 class="mb-2 blue--text">
                {{ kpis.realisationRate.toFixed(1) }}%
              </h3>
              <h4 class="mb-0 blue--text text--lighten-1 fs-14">
                {{ kpis.totalScans | toCurrency }} / {{ kpis.potentiel | toCurrency }} prévus
              </h4>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- POS actifs -->
        <v-col cols="12" lg="3">
          <v-card class="rounded-lg shadow">
            <v-card-title class="pb-0 text-no-wrap">
              <span class="fs-15">POS actifs</span>
              <v-spacer/>
              <v-avatar color="green" size="38">
                <v-icon dark>mdi-store</v-icon>
              </v-avatar>
            </v-card-title>
            <v-card-text>
              <h3 class="mb-2 green--text">
                {{ kpis.activePos | toCurrency }}
                <small class="green--text text--lighten-1 fs-13">/ {{ kpis.totalPosReferenced | toCurrency }}</small>
              </h3>
              <h4 class="mb-0 green--text text--lighten-1 fs-14">
                Taux d'activation : <strong>{{ kpis.activationRate.toFixed(1) }}%</strong>
              </h4>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Consommateurs uniques -->
        <v-col cols="12" lg="3">
          <v-card class="rounded-lg shadow">
            <v-card-title class="pb-0 text-no-wrap">
              <span class="fs-15">Consommateurs uniques</span>
              <v-spacer/>
              <v-avatar color="pink" size="38">
                <v-icon dark>mdi-account-group</v-icon>
              </v-avatar>
            </v-card-title>
            <v-card-text>
              <h3 class="mb-2 pink--text">
                {{ kpis.uniqueConsumers | toCurrency }}
              </h3>
              <h4 class="mb-0 pink--text text--lighten-1 fs-14">
                {{ kpis.avgScansPerConsumer.toFixed(1) }} paquets / conso.
              </h4>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Coût total -->
        <v-col cols="12" lg="3">
          <v-card class="rounded-lg shadow">
            <v-card-title class="pb-0 text-no-wrap">
              <span class="fs-15">Coût total</span>
              <v-spacer/>
              <v-avatar color="deep-purple" size="38">
                <v-icon dark>mdi-cash-multiple</v-icon>
              </v-avatar>
            </v-card-title>
            <v-card-text>
              <h3 class="mb-2 deep-purple--text">
                {{ kpis.totalCost | toCurrency }}
                <small class="deep-purple--text text--lighten-1 fs-13">DA</small>
              </h3>
              <h4 class="mb-0 deep-purple--text text--lighten-1 fs-14">
                ≈ {{ kpis.avgCostPerScan.toFixed(1) }} DA / scan
              </h4>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Incentive POS -->
        <v-col cols="12" lg="3">
          <v-card class="rounded-lg shadow">
            <v-card-title class="pb-0 text-no-wrap">
              <span class="fs-15">Incentive POS</span>
              <v-spacer/>
              <v-avatar color="orange" size="38">
                <v-icon dark>mdi-store-cog</v-icon>
              </v-avatar>
            </v-card-title>
            <v-card-text>
              <h3 class="mb-2 orange--text">
                {{ kpis.totalPosWinnings | toCurrency }}
                <small class="orange--text text--lighten-1 fs-13">DA</small>
              </h3>
              <h4 class="mb-0 orange--text text--lighten-1 fs-14">
                20 DA × paquets vendus
              </h4>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Gains consommateurs -->
        <v-col cols="12" lg="3">
          <v-card class="rounded-lg shadow">
            <v-card-title class="pb-0 text-no-wrap">
              <span class="fs-15">Gains consommateurs</span>
              <v-spacer/>
              <v-avatar color="red" size="38">
                <v-icon dark>mdi-gift</v-icon>
              </v-avatar>
            </v-card-title>
            <v-card-text>
              <h3 class="mb-2 red--text">
                {{ kpis.totalUserWinnings | toCurrency }}
                <small class="red--text text--lighten-1 fs-13">DA</small>
              </h3>
              <h4 class="mb-0 red--text text--lighten-1 fs-14">
                {{ kpis.winnersCount | toCurrency }} gagnants
              </h4>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Gains Offre 3 (1000 DA tous les 3 paquets) -->
        <v-col cols="12" lg="3">
          <v-card class="rounded-lg shadow">
            <v-card-title class="pb-0 text-no-wrap">
              <span class="fs-15">Gains Offre 3</span>
              <v-spacer/>
              <v-avatar color="teal" size="38">
                <v-icon dark>mdi-cash-100</v-icon>
              </v-avatar>
            </v-card-title>
            <v-card-text>
              <h3 class="mb-2 teal--text">
                {{ kpis.offre3Gains1000 | toCurrency }}
                <small class="teal--text text--lighten-1 fs-13">× 1000 DA</small>
              </h3>
              <h4 class="mb-0 teal--text text--lighten-1 fs-14">
                {{ kpis.offre3PaquetsToNext }} paquet(s) avant prochain gain
              </h4>
            </v-card-text>
          </v-card>
        </v-col>

      </v-row>

      <!-- ========== PACKS BY PERIOD (4 CARDS) ========== -->
      <v-row v-if="isLoading">
        <v-col cols="12" lg="3" v-for="item in 4" :key="'period-skel-' + item">
          <v-card class="rounded-lg shadow">
            <v-card-text>
              <v-skeleton-loader type="article"></v-skeleton-loader>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-else>
        <v-col
            v-for="period in periodCards"
            :key="period.key"
            cols="12"
            sm="6"
            lg="3"
        >
          <v-card class="rounded-lg shadow">
            <v-card-title class="pb-0 text-no-wrap">
              <span class="fs-15">{{ period.label }}</span>
              <v-spacer/>
              <v-avatar :color="period.color" size="38">
                <v-icon dark>{{ period.icon }}</v-icon>
              </v-avatar>
            </v-card-title>
            <v-card-text>
              <h3 class="mb-3" :class="period.color + '--text'">
                {{ period.total | toCurrency }}
                <small :class="period.color + '--text text--lighten-1 fs-13'">paquets</small>
              </h3>

              <div class="d-flex flex-column" style="gap: 6px;">
                <div class="d-flex align-center justify-space-between">
                  <span class="fs-13">
                    <v-icon small color="blue">mdi-circle</v-icon>
                    Offre 1
                  </span>
                  <strong class="blue--text fs-14">{{ period.byOffre['OFFRE 1'] | toCurrency }}</strong>
                </div>
                <div class="d-flex align-center justify-space-between">
                  <span class="fs-13">
                    <v-icon small color="deep-purple">mdi-circle</v-icon>
                    Offre 2
                  </span>
                  <strong class="deep-purple--text fs-14">{{ period.byOffre['OFFRE 2'] | toCurrency }}</strong>
                </div>
                <div class="d-flex align-center justify-space-between">
                  <span class="fs-13">
                    <v-icon small color="teal">mdi-circle</v-icon>
                    Offre 3
                  </span>
                  <strong class="teal--text fs-14">{{ period.byOffre['OFFRE 3'] | toCurrency }}</strong>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- ========== WILAYAS BREAKDOWN ========== -->
      <v-row v-if="isLoading">
        <v-col cols="12">
          <v-card class="rounded-lg shadow">
            <v-card-text>
              <v-skeleton-loader type="card"></v-skeleton-loader>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-else>
        <v-col cols="12">
          <v-card class="rounded-lg shadow">
            <v-card-title>Paquets vendus par wilaya</v-card-title>
            <v-divider/>
            <v-card-text>
              <v-row>
                <v-col
                    v-for="(data, wilaya) in wilayasBreakdown"
                    :key="wilaya"
                    cols="12"
                    sm="6"
                    md="4"
                    lg="3"
                >
                  <v-card class="rounded-lg" outlined flat>
                    <v-card-title class="pb-0 text-no-wrap">
                      <span class="fs-15">{{ wilaya }}</span>
                      <v-spacer/>
                      <v-avatar color="info" size="38">
                        <v-icon dark>mdi-map-marker</v-icon>
                      </v-avatar>
                    </v-card-title>
                    <v-card-text>
                      <h4 class="mb-2 indigo--text">
                        Paquets : {{ data.scans | toCurrency }}
                        <small class="indigo--text text--lighten-1">
                          / {{ data.potentiel | toCurrency }} prévus
                        </small>
                      </h4>
                      <h4 class="mb-2 indigo--text">
                        POS actifs :
                        <strong>{{ data.activePos }}</strong>
                        <small class="indigo--text text--lighten-1">
                          / {{ data.totalPos }}
                        </small>
                      </h4>
                      <h4 class="mb-0 indigo--text">
                        Taux réal. :
                        <strong>{{ data.potentiel ? (data.scans / data.potentiel * 100).toFixed(1) : 0 }}%</strong>
                      </h4>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- ========== CHARTS ========== -->
      <v-row>

        <!-- Évolution paquets vendus avec toggle Jour/Semaine/Mois -->
        <v-col cols="12" lg="8">
          <v-card class="rounded-lg shadow">
            <v-card-title>
              Évolution des paquets vendus
              <v-spacer/>
              <v-btn-toggle
                  v-model="evolutionGranularity"
                  mandatory
                  dense
                  color="primary"
                  borderless
              >
                <v-btn small value="day" class="text-none">
                  <v-icon left small>mdi-calendar-today</v-icon>
                  Jour
                </v-btn>
                <v-btn small value="week" class="text-none">
                  <v-icon left small>mdi-calendar-week</v-icon>
                  Semaine
                </v-btn>
                <v-btn small value="month" class="text-none">
                  <v-icon left small>mdi-calendar-month</v-icon>
                  Mois
                </v-btn>
              </v-btn-toggle>
            </v-card-title>
            <v-divider/>
            <v-card-text>
              <v-skeleton-loader v-if="isLoading" type="image"></v-skeleton-loader>
              <div v-else class="chart-wrap">
                <canvas ref="dailyChart"></canvas>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Offre distribution -->
        <v-col cols="12" lg="4">
          <v-card class="rounded-lg shadow">
            <v-card-title>Répartition par offre</v-card-title>
            <v-divider/>
            <v-card-text>
              <v-skeleton-loader v-if="isLoading" type="image"></v-skeleton-loader>
              <div v-else class="chart-wrap">
                <canvas ref="offreChart"></canvas>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

      </v-row>

      <v-row>

        <!-- Top 10 POS -->
        <v-col cols="12" lg="6">
          <v-card class="rounded-lg shadow">
            <v-card-title>Top 10 POS (par paquets vendus)</v-card-title>
            <v-divider/>
            <v-card-text>
              <v-skeleton-loader v-if="isLoading" type="image"></v-skeleton-loader>
              <div v-else class="chart-wrap">
                <canvas ref="topPosChart"></canvas>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Segment comparison -->
        <v-col cols="12" lg="6">
          <v-card class="rounded-lg shadow">
            <v-card-title>Performance par segment (P1 / P2 / P3)</v-card-title>
            <v-divider/>
            <v-card-text>
              <v-skeleton-loader v-if="isLoading" type="image"></v-skeleton-loader>
              <div v-else class="chart-wrap">
                <canvas ref="segmentChart"></canvas>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

      </v-row>

      <!-- ========== DETAILED TABLES (TABS) ========== -->
      <v-row>
        <v-col cols="12">
          <v-card class="rounded-lg shadow">
            <v-tabs v-model="tab" color="primary" background-color="transparent" grow>
              <v-tab tab-value="pos">
                <v-icon left>mdi-store</v-icon>
                Détail par POS
              </v-tab>
              <v-tab tab-value="transactions">
                <v-icon left>mdi-clock-outline</v-icon>
                Transactions ({{ filteredUsages.length | toCurrency }})
              </v-tab>
            </v-tabs>
            <v-divider/>

            <!-- ===== TAB 1 : POS detail ===== -->
            <div v-if="tab === 'pos'">
              <v-card-title>
                <v-spacer/>
                <v-text-field
                    v-model="tableSearch"
                    append-icon="mdi-magnify"
                    label="Rechercher (code POS, QR code…)"
                    single-line
                    hide-details
                    outlined
                    dense
                    clearable
                    style="max-width: 320px;"
                    class="mr-2"
                />
                <v-btn color="success" depressed small class="text-none" @click="exportPosCsv">
                  <v-icon left small>mdi-download</v-icon>
                  Exporter CSV
                </v-btn>
              </v-card-title>
              <v-divider/>
              <v-card-text class="pa-0">
                <v-skeleton-loader v-if="isLoading" type="table"></v-skeleton-loader>
                <v-data-table
                    v-else
                    :headers="tableHeaders"
                    :items="tableItems"
                    :search="tableSearch"
                    :items-per-page="tablePerPage"
                    :footer-props="{'items-per-page-options': [10, 25, 50, 100, -1]}"
                    dense
                >
                  <template v-slot:item.region="{item}">
                    <v-chip :color="regionColor(item.region)" dark x-small label>{{ item.region }}</v-chip>
                  </template>
                  <template v-slot:item.segment="{item}">
                    <v-chip :color="segmentColor(item.segment)" dark x-small label outlined>{{ item.segment }}</v-chip>
                  </template>
                  <template v-slot:item.offre="{item}">
                    <v-chip :color="offreColor(item.offre)" dark x-small label>{{ item.offre }}</v-chip>
                  </template>
                  <template v-slot:item.nom_compte="{item}">
                    <div class="text-caption font-weight-medium" style="max-width: 180px;">{{ item.nom_compte }}</div>
                  </template>
                  <template v-slot:item.commune="{item}">
                    <span class="text-caption">{{ item.commune }}</span>
                  </template>
                  <template v-slot:item.scans="{item}">
                    <strong class="green--text">
                      <v-icon color="green" x-small>mdi-circle</v-icon>
                      {{ item.scans | toCurrency }}
                    </strong>
                  </template>
                  <template v-slot:item.realisation="{item}">
                    <v-progress-linear
                        :value="Math.min(item.realisation, 100)"
                        :color="item.realisation >= 80 ? 'green' : item.realisation >= 50 ? 'orange' : 'red'"
                        height="20"
                        rounded
                    >
                      <template v-slot:default>
                        <strong class="white--text fs-12">{{ item.realisation.toFixed(0) }}%</strong>
                      </template>
                    </v-progress-linear>
                  </template>
                  <template v-slot:item.potentiel="{item}">
                    {{ item.potentiel | toCurrency }}
                  </template>
                  <template v-slot:item.posWinnings="{item}">
                    <strong class="orange--text">{{ item.posWinnings | toCurrency }}</strong>
                  </template>
                  <template v-slot:item.userWinnings="{item}">
                    <strong class="purple--text">{{ item.userWinnings | toCurrency }}</strong>
                  </template>
                  <template v-slot:item.qrCodes="{item}">
                    <div class="text-caption" style="max-width: 180px;">
                      <div v-for="qr in item.qrCodes" :key="qr" class="font-weight-medium">{{ qr }}</div>
                    </div>
                  </template>
                </v-data-table>
              </v-card-text>
            </div>

            <!-- ===== TAB 2 : Transactions (par heure) ===== -->
            <div v-if="tab === 'transactions'">
              <v-card-title>
                <v-spacer/>
                <v-text-field
                    v-model="txSearch"
                    append-icon="mdi-magnify"
                    label="Rechercher (code, QR, compte…)"
                    single-line
                    hide-details
                    outlined
                    dense
                    clearable
                    style="max-width: 320px;"
                    class="mr-2"
                />
                <v-btn color="success" depressed small class="text-none" @click="exportTransactionsCsv">
                  <v-icon left small>mdi-download</v-icon>
                  Exporter CSV ({{ filteredUsages.length | toCurrency }})
                </v-btn>
              </v-card-title>
              <v-divider/>
              <v-card-text class="pa-0">
                <v-skeleton-loader v-if="isLoading" type="table"></v-skeleton-loader>
                <v-data-table
                    v-else
                    :headers="transactionsHeaders"
                    :items="transactionsItems"
                    :search="txSearch"
                    :items-per-page="25"
                    :footer-props="{'items-per-page-options': [25, 50, 100, 200, 500, -1]}"
                    :sort-by.sync="txSortBy"
                    :sort-desc.sync="txSortDesc"
                    dense
                >
                  <template v-slot:item.date="{item}">
                    <strong class="info--text">{{ item.date }}</strong>
                  </template>
                  <template v-slot:item.time="{item}">
                    <span class="font-weight-medium">{{ item.time }}</span>
                  </template>
                  <template v-slot:item.salepoint_code="{item}">
                    <span v-if="item.salepoint_code" class="font-weight-medium">{{ item.salepoint_code }}</span>
                    <span v-else class="grey--text text--darken-1 font-italic">non référencé</span>
                  </template>
                  <template v-slot:item.nom_compte="{item}">
                    <div v-if="item.nom_compte" class="text-caption font-weight-medium" style="max-width: 160px;">{{ item.nom_compte }}</div>
                    <span v-else class="grey--text">—</span>
                  </template>
                  <template v-slot:item.region="{item}">
                    <v-chip v-if="item.region" :color="regionColor(item.region)" dark x-small label>{{ item.region }}</v-chip>
                    <span v-else class="grey--text">—</span>
                  </template>
                  <template v-slot:item.wilaya="{item}">
                    <span v-if="item.wilaya">{{ item.wilaya }}</span>
                    <span v-else class="grey--text">—</span>
                  </template>
                  <template v-slot:item.segment="{item}">
                    <v-chip v-if="item.segment" :color="segmentColor(item.segment)" dark x-small label outlined>{{ item.segment }}</v-chip>
                    <span v-else class="grey--text">—</span>
                  </template>
                  <template v-slot:item.offre="{item}">
                    <v-chip v-if="item.offre" :color="offreColor(item.offre)" dark x-small label>{{ item.offre }}</v-chip>
                    <span v-else class="grey--text">—</span>
                  </template>
                  <template v-slot:item.source_code="{item}">
                    <span v-if="item.source_code" class="font-weight-medium text-caption">{{ item.source_code }}</span>
                    <span v-else class="grey--text">—</span>
                  </template>
                  <template v-slot:item.user_winning_amount="{item}">
                    <strong v-if="Number(item.user_winning_amount) > 0" class="purple--text">
                      +{{ item.user_winning_amount | toCurrency }} DA
                    </strong>
                    <span v-else class="grey--text">0</span>
                  </template>
                  <template v-slot:item.salepoint_winning_amount="{item}">
                    <strong v-if="Number(item.salepoint_winning_amount) > 0" class="orange--text">
                      +{{ item.salepoint_winning_amount | toCurrency }} DA
                    </strong>
                    <span v-else class="grey--text">0</span>
                  </template>
                  <template v-slot:item.status="{item}">
                    <v-chip v-if="Number(item.user_winning_amount) > 0" color="green" dark x-small label>
                      <v-icon left x-small>mdi-check</v-icon>
                      Gagnant
                    </v-chip>
                    <v-chip v-else-if="Number(item.salepoint_winning_amount) > 0" color="orange" dark x-small label>
                      POS seul
                    </v-chip>
                    <v-chip v-else color="grey" dark x-small label>Sans gain</v-chip>
                  </template>
                  <template v-slot:item.platform_used="{item}">
                    <v-icon v-if="item.platform_used === 'web'" small color="blue">mdi-web</v-icon>
                    <v-icon v-else-if="item.platform_used === 'mobile'" small color="green">mdi-cellphone</v-icon>
                    <span v-else>{{ item.platform_used }}</span>
                  </template>
                </v-data-table>
              </v-card-text>
            </div>

          </v-card>
        </v-col>
      </v-row>

    </v-container>
  </div>
</template>

<script>
import axios from 'axios'
import {
  Chart,
  BarController,
  BarElement,
  DoughnutController,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
} from 'chart.js'
import {
  POS_BY_CODE,
  REGIONS,
  WILAYAS,
  SEGMENTS,
  OFFRES,
  POTENTIEL_TOTAL,
} from './posReference'

// Register Chart.js components once
Chart.register(
    BarController, BarElement, DoughnutController, ArcElement,
    CategoryScale, LinearScale, Tooltip, Legend, Title
)

// ============ CONFIG ============
const API_URL = 'https://api.gifty.opus.win/api/v1/boosting-methods/usagesAll'
const BOOSTING_METHOD_ID = 1
const PER_PAGE = 500   // large batch for dashboard
const MAX_PAGES = 20   // safety cap = up to 10 000 records

// Début de l'opération QR Code. Les usages antérieurs appartiennent à des
// opérations précédentes et fausseraient tous les totaux : ils sont écartés
// dès le chargement, donc avant tout KPI, graphique ou export.
//
// Date locale : `new Date(2026, 9, 12)` et non une chaîne ISO, qui serait
// interprétée en UTC et décalerait la borne de quelques heures.
const OPERATION_START = new Date(2026, 9, 12, 0, 0, 0, 0)   // 12 octobre 2026

export default {
  name: 'QrDashboard',

  data() {
    return {
      overlay: false,
      isLoading: false,

      allUsages: [],          // enriched usage records, depuis OPERATION_START
      lastFetchedAt: null,

      // Usages écartés car antérieurs au début d'opération ou sans date.
      // Affiché pour que la borne reste visible, et non appliquée en silence.
      excludedBeforeStart: 0,
      operationStart: OPERATION_START,

      filters: {
        region: null,
        segment: null,
        offre: null,
        wilaya: null,
        period: 'all',        // 'today' | 'week' | 'month' | 'all'
      },

      // Table
      tableSearch: '',
      tablePerPage: 10,

      // Tabs
      tab: 'pos',

      // Evolution chart granularity
      evolutionGranularity: 'day',  // 'day' | 'week' | 'month'

      // Transactions table
      txSearch: '',
      txSortBy: 'used_at',
      txSortDesc: true,

      // Chart instances (non-reactive)
      charts: {
        daily: null,
        offre: null,
        topPos: null,
        segment: null,   // Segment chart bucket
      },

      regionOptions: REGIONS,
      segmentOptions: SEGMENTS,
      offreOptions: OFFRES,
      wilayaOptions: WILAYAS,
      periodOptions: [
        { text: "Aujourd'hui", value: 'today' },
        { text: 'Cette semaine', value: 'week' },
        { text: 'Ce mois', value: 'month' },
        { text: 'Tout', value: 'all' },
      ],

      tableHeaders: [
        { text: 'Code POS', value: 'code' },
        { text: 'Nom du compte', value: 'nom_compte' },
        { text: 'QR code POS', value: 'qrCodes', sortable: false },
        { text: 'Région', value: 'region' },
        { text: 'Wilaya', value: 'wilaya' },
        { text: 'Commune', value: 'commune' },
        { text: 'Segment', value: 'segment' },
        { text: 'Offre', value: 'offre' },
        { text: 'Potentiel', value: 'potentiel', align: 'end' },
        { text: 'Paquets vendus', value: 'scans', align: 'end' },
        { text: 'Taux réal.', value: 'realisation', align: 'center', width: 140 },
        { text: 'Incentive POS', value: 'posWinnings', align: 'end' },
        { text: 'Gains conso.', value: 'userWinnings', align: 'end' },
      ],

      transactionsHeaders: [
        { text: 'Date', value: 'date', width: 110 },
        { text: 'Heure', value: 'time', width: 90 },
        { text: 'Code POS', value: 'salepoint_code' },
        { text: 'Nom du compte', value: 'nom_compte' },
        { text: 'Région', value: 'region' },
        { text: 'Wilaya', value: 'wilaya' },
        { text: 'Segment', value: 'segment' },
        { text: 'Offre', value: 'offre' },
        { text: 'QR code', value: 'source_code' },
        { text: 'Gain conso.', value: 'user_winning_amount', align: 'end' },
        { text: 'Gain POS', value: 'salepoint_winning_amount', align: 'end' },
        { text: 'Statut', value: 'status', align: 'center' },
        { text: 'Plateforme', value: 'platform_used', align: 'center' },
        { text: 'ID', value: 'id', align: 'end' },
      ],
    }
  },

  computed: {
    // Wilaya dropdown options filtered by the currently-selected region
    // Libellé de la borne de début, pour l'en-tête.
    operationStartLabel() {
      return this.operationStart.toLocaleDateString('fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric',
      })
    },

    filteredWilayaOptions() {
      if (!this.filters.region) return this.wilayaOptions
      const wilayasInRegion = [...new Set(
          Object.values(POS_BY_CODE)
              .filter(p => p.region === this.filters.region)
              .map(p => p.wilaya)
      )].sort()
      return wilayasInRegion
    },

    // Any filter active? (helpers for the reset button)
    hasActiveFilters() {
      const f = this.filters
      return !!(f.region || f.segment || f.offre || f.wilaya) || f.period !== 'all'
    },

    // Usages after ALL filters applied
    filteredUsages() {
      const now = new Date()
      return this.allUsages.filter(u => {
        if (this.filters.region && u.region !== this.filters.region) return false
        if (this.filters.segment && u.segment !== this.filters.segment) return false
        if (this.filters.offre && u.offre !== this.filters.offre) return false
        if (this.filters.wilaya && u.wilaya !== this.filters.wilaya) return false

        if (this.filters.period !== 'all' && u.usedAtDate) {
          const diffDays = (now - u.usedAtDate) / (1000 * 60 * 60 * 24)
          if (this.filters.period === 'today' && diffDays > 1) return false
          if (this.filters.period === 'week' && diffDays > 7) return false
          if (this.filters.period === 'month' && diffDays > 30) return false
        }
        return true
      })
    },

    // Main KPIs object — recomputed reactively on filter change
    kpis() {
      const usages = this.filteredUsages
      const totalScans = usages.length
      const totalUserWinnings = usages.reduce((s, u) => s + Number(u.user_winning_amount || 0), 0)
      const totalPosWinnings = usages.reduce((s, u) => s + Number(u.salepoint_winning_amount || 0), 0)
      const winnersCount = usages.filter(u => Number(u.user_winning_amount) > 0).length
      const uniqueConsumers = new Set(usages.map(u => u.account_id).filter(Boolean)).size
      const activePosSet = new Set(usages.map(u => u.salepoint_code).filter(Boolean))

      // Potentiel according to filters (region/segment/offre/wilaya)
      const potentiel = this.getFilteredPotentiel()
      const totalPosReferenced = this.getFilteredPosCount()

      // Offre 3 gain rate
      // Offre 3 : gains de 1000 DA (déclenchés tous les 3 paquets scannés dans un POS Offre 3)
      const offre3 = usages.filter(u => u.offre === 'OFFRE 3')
      const offre3Gains1000 = offre3.filter(u => Number(u.user_winning_amount) === 1000).length
      // Paquets Offre 3 restant avant prochain gain (compteur cyclique modulo 3)
      // Note : agrégat global — le vrai compteur est par consommateur côté back
      const offre3PaquetsToNext = offre3.length ? (3 - (offre3.length % 3)) % 3 || 3 : 0

      // Today scans
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const scansToday = usages.filter(u => u.usedAtDate >= today).length

      // Packs sold by offre
      const packsByOffre = { 'OFFRE 1': 0, 'OFFRE 2': 0, 'OFFRE 3': 0 }
      usages.forEach(u => {
        if (u.offre && packsByOffre[u.offre] !== undefined) packsByOffre[u.offre] += 1
      })

      return {
        totalScans,
        scansToday,
        packsByOffre,
        potentiel,
        realisationRate: potentiel ? (totalScans / potentiel * 100) : 0,
        activePos: activePosSet.size,
        totalPosReferenced,
        activationRate: totalPosReferenced ? (activePosSet.size / totalPosReferenced * 100) : 0,
        uniqueConsumers,
        avgScansPerConsumer: uniqueConsumers ? (totalScans / uniqueConsumers) : 0,
        totalUserWinnings,
        totalPosWinnings,
        totalCost: totalUserWinnings + totalPosWinnings,
        avgCostPerScan: totalScans ? ((totalUserWinnings + totalPosWinnings) / totalScans) : 0,
        winnersCount,
        offre3Gains1000,
        offre3PaquetsToNext,
      }
    },

    // Period breakdown cards: today / this week / this month / total
    // ⚠ Indépendant du filtre période (sinon les 4 cartes seraient redondantes).
    // Respecte par contre les filtres region / segment / offre / wilaya.
    periodCards() {
      const baseUsages = this.allUsages.filter(u => {
        if (this.filters.region && u.region !== this.filters.region) return false
        if (this.filters.segment && u.segment !== this.filters.segment) return false
        if (this.filters.offre && u.offre !== this.filters.offre) return false
        if (this.filters.wilaya && u.wilaya !== this.filters.wilaya) return false
        return true
      })

      const now = new Date()
      const startOfToday = new Date(now)
      startOfToday.setHours(0, 0, 0, 0)

      // Start of week = Monday 00:00
      const startOfWeek = new Date(startOfToday)
      const dayOfWeek = startOfWeek.getDay() || 7   // Sunday → 7
      startOfWeek.setDate(startOfWeek.getDate() - (dayOfWeek - 1))

      // Start of month = day 1, 00:00
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0)

      const emptyByOffre = () => ({ 'OFFRE 1': 0, 'OFFRE 2': 0, 'OFFRE 3': 0 })

      const buckets = {
        today: { total: 0, byOffre: emptyByOffre() },
        week: { total: 0, byOffre: emptyByOffre() },
        month: { total: 0, byOffre: emptyByOffre() },
        all: { total: 0, byOffre: emptyByOffre() },
      }

      baseUsages.forEach(u => {
        if (!u.usedAtDate || isNaN(u.usedAtDate)) return
        const offre = u.offre
        const inToday = u.usedAtDate >= startOfToday
        const inWeek = u.usedAtDate >= startOfWeek
        const inMonth = u.usedAtDate >= startOfMonth

        buckets.all.total += 1
        if (offre && buckets.all.byOffre[offre] !== undefined) buckets.all.byOffre[offre] += 1

        if (inMonth) {
          buckets.month.total += 1
          if (offre && buckets.month.byOffre[offre] !== undefined) buckets.month.byOffre[offre] += 1
        }
        if (inWeek) {
          buckets.week.total += 1
          if (offre && buckets.week.byOffre[offre] !== undefined) buckets.week.byOffre[offre] += 1
        }
        if (inToday) {
          buckets.today.total += 1
          if (offre && buckets.today.byOffre[offre] !== undefined) buckets.today.byOffre[offre] += 1
        }
      })

      return [
        { key: 'today', label: "Aujourd'hui", icon: 'mdi-calendar-today', color: 'indigo',
          total: buckets.today.total, byOffre: buckets.today.byOffre },
        { key: 'week', label: 'Cette semaine', icon: 'mdi-calendar-week', color: 'blue',
          total: buckets.week.total, byOffre: buckets.week.byOffre },
        { key: 'month', label: 'Ce mois', icon: 'mdi-calendar-month', color: 'green',
          total: buckets.month.total, byOffre: buckets.month.byOffre },
        { key: 'all', label: 'Total', icon: 'mdi-package-variant-closed', color: 'deep-purple',
          total: buckets.all.total, byOffre: buckets.all.byOffre },
      ]
    },

    // Wilaya breakdown cards
    wilayasBreakdown() {
      const out = {}
      for (const w of WILAYAS) {
        if (this.filters.wilaya && this.filters.wilaya !== w) continue
        out[w] = { scans: 0, potentiel: 0, activePos: 0, totalPos: 0, posSet: new Set() }
      }
      // Count total POS + potentiel per wilaya (from reference, filtered by region/segment/offre if any)
      Object.values(POS_BY_CODE).forEach(p => {
        if (!out[p.wilaya]) return
        if (this.filters.region && p.region !== this.filters.region) return
        if (this.filters.segment && p.segment !== this.filters.segment) return
        if (this.filters.offre && p.offre !== this.filters.offre) return
        out[p.wilaya].totalPos += 1
        out[p.wilaya].potentiel += p.potentiel
      })
      // Count paquets vendus
      this.filteredUsages.forEach(u => {
        if (!u.wilaya || !out[u.wilaya]) return
        out[u.wilaya].scans += 1
        if (u.salepoint_code) out[u.wilaya].posSet.add(u.salepoint_code)
      })
      // Finalize
      Object.values(out).forEach(d => { d.activePos = d.posSet.size; delete d.posSet })
      // Drop wilayas with 0 potentiel & 0 scans
      Object.keys(out).forEach(k => {
        if (out[k].totalPos === 0 && out[k].scans === 0) delete out[k]
      })
      return out
    },

    // Detailed table items — aggregated by POS
    tableItems() {
      const byPos = {}
      // Seed with referenced POS matching current filters
      Object.values(POS_BY_CODE).forEach(p => {
        if (this.filters.region && p.region !== this.filters.region) return
        if (this.filters.segment && p.segment !== this.filters.segment) return
        if (this.filters.offre && p.offre !== this.filters.offre) return
        if (this.filters.wilaya && p.wilaya !== this.filters.wilaya) return
        byPos[p.code] = {
          code: p.code,
          nom_compte: p.nom_compte,
          region: p.region,
          wilaya: p.wilaya,
          commune: p.commune,
          segment: p.segment,
          offre: p.offre,
          potentiel: p.potentiel,
          scans: 0,
          posWinnings: 0,
          userWinnings: 0,
          qrCodes: new Set(),
          realisation: 0,
        }
      })
      // Aggregate usages
      this.filteredUsages.forEach(u => {
        const key = u.salepoint_code || '(non référencé)'
        if (!byPos[key]) {
          byPos[key] = {
            code: key,
            nom_compte: u.nom_compte || '—',
            region: u.region || '—',
            wilaya: u.wilaya || '—',
            commune: u.commune || '—',
            segment: u.segment || '—',
            offre: u.offre || '—',
            potentiel: 0,
            scans: 0,
            posWinnings: 0,
            userWinnings: 0,
            qrCodes: new Set(),
            realisation: 0,
          }
        }
        byPos[key].scans += 1
        byPos[key].posWinnings += Number(u.salepoint_winning_amount || 0)
        byPos[key].userWinnings += Number(u.user_winning_amount || 0)
        if (u.source_code) byPos[key].qrCodes.add(u.source_code)
      })
      // Finalize
      return Object.values(byPos).map(p => ({
        ...p,
        qrCodes: [...p.qrCodes].slice(0, 3),
        realisation: p.potentiel ? (p.scans / p.potentiel * 100) : (p.scans > 0 ? 100 : 0),
      }))
    },

    // Transactions table — one row per usage, with date/time split
    transactionsItems() {
      return this.filteredUsages.map(u => {
        let date = '—', time = '—'
        if (u.usedAtDate && !isNaN(u.usedAtDate)) {
          date = u.usedAtDate.toLocaleDateString('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric'
          })
          time = u.usedAtDate.toLocaleTimeString('fr-FR', {
            hour: '2-digit', minute: '2-digit', second: '2-digit'
          })
        }
        return {
          id: u.id,
          used_at: u.used_at,               // raw, used for sorting
          date,
          time,
          salepoint_code: u.salepoint_code,
          nom_compte: u.nom_compte,
          region: u.region,
          wilaya: u.wilaya,
          commune: u.commune,
          segment: u.segment,
          offre: u.offre,
          source_code: u.source_code,
          user_winning_amount: u.user_winning_amount,
          salepoint_winning_amount: u.salepoint_winning_amount,
          platform_used: u.platform_used,
          account_id: u.account_id,
          salepoint_account_id: u.salepoint_account_id,
          user_transaction_id: u.user_transaction_id,
          salepoint_transaction_id: u.salepoint_transaction_id,
        }
      })
    },
  },

  watch: {
    // Re-render charts when filtered data changes
    filteredUsages: {
      handler() {
        this.$nextTick(() => this.renderAllCharts())
      },
      deep: false,
    },
    // Re-render only the daily chart when granularity changes (lighter)
    evolutionGranularity() {
      this.$nextTick(() => this.renderDailyChart())
    },
  },

  methods: {

    // ============ API FETCHING ============
    async fetchUsages() {
      this.isLoading = true
      this.overlay = true
      this.allUsages = []
      try {
        let page = 1
        let keepGoing = true
        const all = []

        // Déduplication par identifiant d'usage.
        //
        // L'endpoint /boosting-methods/usagesAll ignore aujourd'hui `page` et
        // `perPage` : il fait un ->get() et renvoie TOUT le jeu de données à
        // chaque appel. Sans ce garde-fou, la boucle ci-dessous redemandait la
        // « page suivante », recevait les mêmes lignes et les empilait — chaque
        // paquet était donc compté jusqu'à MAX_PAGES fois dès que le volume
        // dépassait PER_PAGE.
        //
        // Dédupliquer plutôt que supprimer la boucle garde ce code juste dans
        // les deux cas : si l'API se met à paginer pour de bon, les pages
        // successives apportent des lignes nouvelles et tout s'enchaîne.
        const seen = new Set()

        while (keepGoing && page <= MAX_PAGES) {
          const response = await axios.get(API_URL, {
            params: {
              page,
              perPage: PER_PAGE,
              boosting_method_id: BOOSTING_METHOD_ID,
            },
            headers: this.buildHeaders(),
          })

          // Response may be { data: [...] } or { data: { data: [...] } } (Laravel-style)
          const payload = response.data
          const items =
              Array.isArray(payload) ? payload
                  : Array.isArray(payload?.data) ? payload.data
                      : Array.isArray(payload?.data?.data) ? payload.data.data
                          : []

          const fresh = items.filter(item => {
            const key = this.usageKey(item)
            if (seen.has(key)) return false
            seen.add(key)
            return true
          })

          all.push(...fresh)

          if (items.length === 0) {
            // Plus rien à lire.
            keepGoing = false
          } else if (fresh.length === 0) {
            // La page n'apporte aucune ligne nouvelle : l'API renvoie en boucle
            // le même jeu de données. Inutile d'insister.
            keepGoing = false
          } else if (items.length < PER_PAGE) {
            // Dernière page d'une vraie pagination.
            keepGoing = false
          } else {
            page += 1
          }
        }

        // Enrich each usage with POS reference data
        const enriched = all.map(u => this.enrichUsage(u))

        // Borne de début d'opération. Un usage sans date exploitable est écarté
        // lui aussi : il ne peut être rattaché à aucune période, et le garder
        // gonflerait les totaux sans jamais apparaître dans les cartes.
        this.allUsages = enriched.filter(
            u => u.usedAtDate && !isNaN(u.usedAtDate) && u.usedAtDate >= OPERATION_START
        )
        this.excludedBeforeStart = enriched.length - this.allUsages.length

        this.lastFetchedAt = new Date()
        this.$nextTick(() => this.renderAllCharts())
      } catch (err) {
        console.error('Erreur fetch usages:', err)
        if (this.$Progress) this.$Progress.fail()
      } finally {
        this.isLoading = false
        this.overlay = false
      }
    },

    /**
     * Identifiant stable d'un usage, pour la déduplication.
     *
     * `id` suffit en principe. Le repli compose une clé à partir des champs
     * qui identifient un scan — mieux vaut dédupliquer un peu trop qu'ajouter
     * deux fois la même ligne, puisque c'est précisément ce qu'on corrige.
     */
    usageKey(u) {
      if (u && (u.id !== undefined && u.id !== null)) return `id:${u.id}`
      return [
        u?.salepoint_code,
        u?.account_id,
        u?.used_at,
        u?.source_code,
        u?.user_winning_amount,
        u?.salepoint_winning_amount,
      ].join('|')
    },

    buildHeaders() {
      const token = process.env.VUE_APP_BOOSTING_API_TOKEN
          || this.$store?.state?.user?.token
      return token
          ? { Authorization: `Bearer ${token}`, Accept: 'application/json' }
          : { Accept: 'application/json' }
    },

    // Enrich a raw API usage with region/wilaya/segment/offre/… from POS reference
    enrichUsage(u) {
      const posInfo = POS_BY_CODE[u.salepoint_code]
      return {
        ...u,
        usedAtDate: u.used_at ? new Date(String(u.used_at).replace(' ', 'T')) : null,
        region: posInfo?.region || null,
        wilaya: posInfo?.wilaya || null,
        commune: posInfo?.commune || null,
        segment: posInfo?.segment || null,
        nom_compte: posInfo?.nom_compte || null,
        commercial: posInfo?.commercial || null,
        offre: posInfo?.offre || this.deduceOffre(u),
        potentiel: posInfo?.potentiel || 0,
      }
    },

    // Fallback offre deduction if POS not in reference
    // Barème officiel (slide WEST — Incentive POS et consommateur) :
    //   OFFRE 1 → POS 20 DA + Consommateur 20 DA par paquet scanné
    //   OFFRE 2 → POS 20 DA, consommateur ne reçoit rien
    //   OFFRE 3 → POS 20 DA + Consommateur 1000 DA tous les 3 paquets scannés
    //             (donc amt côté conso : 1000 au 3e paquet déclencheur, sinon 0)
    // Un usage avec user_winning_amount = 0 est AMBIGU (OFFRE 2 ou OFFRE 3 intermédiaire)
    // → non déductible sans la référence POS.
    deduceOffre(u) {
      const amt = Number(u.user_winning_amount || 0)
      if (amt === 20) return 'OFFRE 1'
      if (amt === 1000) return 'OFFRE 3'
      return null
    },

    resetFilters() {
      this.filters.region = null
      this.filters.segment = null
      this.filters.offre = null
      this.filters.wilaya = null
      this.filters.period = 'all'
    },

    // Potentiel total for current filter combination
    getFilteredPotentiel() {
      return Object.values(POS_BY_CODE)
          .filter(p => (!this.filters.region || p.region === this.filters.region))
          .filter(p => (!this.filters.segment || p.segment === this.filters.segment))
          .filter(p => (!this.filters.offre || p.offre === this.filters.offre))
          .filter(p => (!this.filters.wilaya || p.wilaya === this.filters.wilaya))
          .reduce((s, p) => s + p.potentiel, 0) || POTENTIEL_TOTAL
    },
    getFilteredPosCount() {
      return Object.values(POS_BY_CODE)
          .filter(p => (!this.filters.region || p.region === this.filters.region))
          .filter(p => (!this.filters.segment || p.segment === this.filters.segment))
          .filter(p => (!this.filters.offre || p.offre === this.filters.offre))
          .filter(p => (!this.filters.wilaya || p.wilaya === this.filters.wilaya))
          .length
    },

    // ============ CHARTS ============
    renderAllCharts() {
      this.renderDailyChart()
      this.renderOffreChart()
      this.renderTopPosChart()
      this.renderSegmentChart()
    },

    renderDailyChart() {
      if (!this.$refs.dailyChart) return
      const granularity = this.evolutionGranularity || 'day'

      // Group usages by bucket key according to granularity
      const buckets = {}
      this.filteredUsages.forEach(u => {
        if (!u.usedAtDate || isNaN(u.usedAtDate)) return
        const key = this.bucketKey(u.usedAtDate, granularity)
        if (!buckets[key]) buckets[key] = { 'OFFRE 1': 0, 'OFFRE 2': 0, 'OFFRE 3': 0 }
        const off = u.offre || 'OFFRE 2'
        buckets[key][off] = (buckets[key][off] || 0) + 1
      })

      const sortedKeys = Object.keys(buckets).sort()
      const labels = sortedKeys.map(k => this.formatBucketLabel(k, granularity))
      const titleByGranularity = {
        day: 'Paquets vendus par jour',
        week: 'Paquets vendus par semaine',
        month: 'Paquets vendus par mois',
      }

      if (this.charts.daily) this.charts.daily.destroy()
      this.charts.daily = new Chart(this.$refs.dailyChart.getContext('2d'), {
        type: 'bar',
        data: {
          labels,
          datasets: [
            { label: 'Offre 1', data: sortedKeys.map(k => buckets[k]['OFFRE 1'] || 0), backgroundColor: '#3FA9F5', borderRadius: 4 },
            { label: 'Offre 2', data: sortedKeys.map(k => buckets[k]['OFFRE 2'] || 0), backgroundColor: '#8B5CF6', borderRadius: 4 },
            { label: 'Offre 3', data: sortedKeys.map(k => buckets[k]['OFFRE 3'] || 0), backgroundColor: '#10B981', borderRadius: 4 },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom' },
            title: {
              display: true,
              text: titleByGranularity[granularity],
              font: { size: 13, weight: 'normal' },
              color: '#666',
              padding: { bottom: 10 },
            },
            tooltip: {
              callbacks: {
                footer: (items) => {
                  const total = items.reduce((s, it) => s + it.parsed.y, 0)
                  return 'Total : ' + total + ' paquets'
                },
              },
            },
          },
          scales: {
            x: { stacked: true, grid: { display: false } },
            y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
          },
        },
      })
    },

    // Compute the bucket key for a date according to granularity
    bucketKey(date, granularity) {
      if (granularity === 'day') {
        // YYYY-MM-DD
        return date.toISOString().slice(0, 10)
      }
      if (granularity === 'week') {
        // ISO week: find Monday of the week, key = "YYYY-Www"
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
        const dayNum = d.getUTCDay() || 7
        d.setUTCDate(d.getUTCDate() + 4 - dayNum)
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
        const weekNum = Math.ceil((((d - yearStart) / 86400000) + 1) / 7)
        return `${d.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`
      }
      if (granularity === 'month') {
        // YYYY-MM
        return date.toISOString().slice(0, 7)
      }
      return date.toISOString().slice(0, 10)
    },

    formatBucketLabel(key, granularity) {
      if (granularity === 'day') {
        const d = new Date(key)
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
      }
      if (granularity === 'week') {
        // key = "YYYY-Www"
        const [year, w] = key.split('-W')
        return `S${w} ${year}`
      }
      if (granularity === 'month') {
        const [year, month] = key.split('-')
        const d = new Date(Number(year), Number(month) - 1, 1)
        return d.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })
      }
      return key
    },

    renderOffreChart() {
      if (!this.$refs.offreChart) return
      const counts = { 'OFFRE 1': 0, 'OFFRE 2': 0, 'OFFRE 3': 0 }
      this.filteredUsages.forEach(u => {
        if (u.offre && counts[u.offre] !== undefined) counts[u.offre] += 1
      })
      if (this.charts.offre) this.charts.offre.destroy()
      this.charts.offre = new Chart(this.$refs.offreChart.getContext('2d'), {
        type: 'doughnut',
        data: {
          labels: ['Offre 1 · 20 DA / paquet', 'Offre 2 · POS seul', 'Offre 3 · 1000 DA / 3 paquets'],
          datasets: [{
            data: Object.values(counts),
            backgroundColor: ['#3FA9F5', '#8B5CF6', '#10B981'],
            borderWidth: 2,
            borderColor: '#fff',
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '62%',
          plugins: {
            legend: { position: 'bottom' },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const total = ctx.dataset.data.reduce((a, b) => a + b, 0)
                  const pct = total ? (ctx.parsed / total * 100).toFixed(1) : 0
                  return `${ctx.label.split(' ·')[0]} : ${ctx.parsed} (${pct}%)`
                },
              },
            },
          },
        },
      })
    },

    renderTopPosChart() {
      if (!this.$refs.topPosChart) return
      const counts = {}
      this.filteredUsages.forEach(u => {
        const key = u.salepoint_code || '(non réf.)'
        counts[key] = (counts[key] || 0) + 1
      })
      const top = Object.entries(counts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10)

      if (this.charts.topPos) this.charts.topPos.destroy()
      this.charts.topPos = new Chart(this.$refs.topPosChart.getContext('2d'), {
        type: 'bar',
        data: {
          labels: top.map(t => t[0]),
          datasets: [{
            label: 'Paquets vendus',
            data: top.map(t => t[1]),
            backgroundColor: top.map(t => {
              const p = POS_BY_CODE[t[0]]
              if (!p) return '#94A3B8'
              // Couleur par région
              if (p.region === 'CENTRE') return '#3FA9F5'
              if (p.region === 'EST') return '#F58220'
              if (p.region === 'OUEST') return '#10B981'
              return '#8B5CF6'
            }),
            borderRadius: 4,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: 'y',
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                afterLabel: (ctx) => {
                  const p = POS_BY_CODE[top[ctx.dataIndex][0]]
                  if (!p) return ''
                  return `${p.region} · ${p.wilaya} · ${p.segment}`
                },
              },
            },
          },
          scales: {
            x: { beginAtZero: true, ticks: { precision: 0 } },
            y: { grid: { display: false } },
          },
        },
      })
    },

    renderSegmentChart() {
      if (!this.$refs.segmentChart) return
      // Segment × POS count from reference (respecting filters)
      const bySeg = { 'P1': { scans: 0, cost: 0, pos: 0, potentiel: 0 }, 'P2': { scans: 0, cost: 0, pos: 0, potentiel: 0 }, 'P3': { scans: 0, cost: 0, pos: 0, potentiel: 0 } }

      // Count POS + potentiel from reference (filters minus segment itself)
      Object.values(POS_BY_CODE).forEach(p => {
        if (!bySeg[p.segment]) return
        if (this.filters.region && p.region !== this.filters.region) return
        if (this.filters.offre && p.offre !== this.filters.offre) return
        if (this.filters.wilaya && p.wilaya !== this.filters.wilaya) return
        bySeg[p.segment].pos += 1
        bySeg[p.segment].potentiel += p.potentiel
      })

      // Count paquets & cost from filteredUsages
      this.filteredUsages.forEach(u => {
        if (!u.segment || !bySeg[u.segment]) return
        bySeg[u.segment].scans += 1
        bySeg[u.segment].cost += Number(u.user_winning_amount || 0) + Number(u.salepoint_winning_amount || 0)
      })

      if (this.charts.segment) this.charts.segment.destroy()
      this.charts.segment = new Chart(this.$refs.segmentChart.getContext('2d'), {
        type: 'bar',
        data: {
          labels: Object.keys(bySeg),
          datasets: [
            { label: 'Paquets vendus', data: Object.values(bySeg).map(s => s.scans), backgroundColor: '#F58220', borderRadius: 4, yAxisID: 'y' },
            { label: 'Potentiel', data: Object.values(bySeg).map(s => s.potentiel), backgroundColor: '#CBD5E1', borderRadius: 4, yAxisID: 'y' },
            { label: 'Coût total (DA)', data: Object.values(bySeg).map(s => s.cost), backgroundColor: '#3FA9F5', borderRadius: 4, yAxisID: 'y1' },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom' },
            tooltip: {
              callbacks: {
                afterBody: (items) => {
                  const seg = items[0].label
                  return `${bySeg[seg].pos} POS`
                },
              },
            },
          },
          scales: {
            x: { grid: { display: false } },
            y: { position: 'left', beginAtZero: true, title: { display: true, text: 'Paquets' } },
            y1: { position: 'right', beginAtZero: true, grid: { drawOnChartArea: false }, title: { display: true, text: 'Coût (DA)' } },
          },
        },
      })
    },

    // ============ HELPERS ============
    formatShortDate(iso) {
      const d = new Date(iso)
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
    },
    regionColor(region) {
      if (region === 'CENTRE') return 'blue'
      if (region === 'EST') return 'orange'
      if (region === 'OUEST') return 'green'
      return 'grey'
    },
    segmentColor(segment) {
      if (segment === 'P1') return 'red darken-1'         // priorité haute
      if (segment === 'P2') return 'amber darken-2'       // priorité moyenne
      if (segment === 'P3') return 'grey darken-1'        // priorité basse
      return 'grey'
    },
    offreColor(offre) {
      if (offre === 'OFFRE 1') return 'blue'
      if (offre === 'OFFRE 2') return 'deep-purple'
      if (offre === 'OFFRE 3') return 'teal'
      return 'grey'
    },

    // ============ EXPORT ============
    exportPosCsv() {
      const rows = this.tableItems
      const headers = [
        'Code POS', 'Nom du compte', 'Région', 'Wilaya', 'Commune',
        'Segment', 'Offre', 'QR codes', 'Potentiel (paquets 2W)',
        'Paquets vendus', 'Taux realisation (%)',
        'Incentive POS (DA)', 'Gains conso. (DA)',
      ]
      const lines = [headers.join(';')]
      rows.forEach(r => {
        lines.push([
          r.code,
          this.csvSafe(r.nom_compte),
          this.csvSafe(r.region),
          this.csvSafe(r.wilaya),
          this.csvSafe(r.commune),
          this.csvSafe(r.segment),
          this.csvSafe(r.offre),
          this.csvSafe((r.qrCodes || []).join('|')),
          r.potentiel,
          r.scans,
          r.realisation.toFixed(1),
          r.posWinnings,
          r.userWinnings,
        ].join(';'))
      })
      this.downloadCsv(lines.join('\n'), `qr-dashboard-pos-${this.todayStamp()}.csv`)
    },

    exportTransactionsCsv() {
      const rows = this.transactionsItems
      const headers = [
        'ID', 'Date', 'Heure', 'Code POS', 'Nom du compte',
        'Région', 'Wilaya', 'Segment', 'Offre',
        'QR code (source)', 'Gain consommateur (DA)', 'Gain POS (DA)',
        'Statut', 'Plateforme', 'Compte consommateur ID', 'Compte POS ID',
        'Transaction conso. ID', 'Transaction POS ID',
      ]
      const lines = [headers.join(';')]
      rows.forEach(r => {
        const status = Number(r.user_winning_amount) > 0 ? 'Gagnant'
            : Number(r.salepoint_winning_amount) > 0 ? 'POS seul' : 'Sans gain'
        lines.push([
          r.id,
          r.date,
          r.time,
          this.csvSafe(r.salepoint_code),
          this.csvSafe(r.nom_compte),
          this.csvSafe(r.region),
          this.csvSafe(r.wilaya),
          this.csvSafe(r.segment),
          this.csvSafe(r.offre),
          this.csvSafe(r.source_code),
          r.user_winning_amount || 0,
          r.salepoint_winning_amount || 0,
          status,
          this.csvSafe(r.platform_used),
          r.account_id || '',
          r.salepoint_account_id || '',
          r.user_transaction_id || '',
          r.salepoint_transaction_id || '',
        ].join(';'))
      })
      this.downloadCsv(lines.join('\n'), `qr-dashboard-transactions-${this.todayStamp()}.csv`)
    },

    csvSafe(v) {
      if (v === null || v === undefined) return ''
      const s = String(v)
      return s.includes(';') || s.includes('"') || s.includes('\n')
          ? '"' + s.replace(/"/g, '""') + '"'
          : s
    },

    todayStamp() {
      const d = new Date()
      return `${d.toISOString().slice(0, 10)}-${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`
    },

    downloadCsv(content, filename) {
      const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    },
  },

  mounted() {
    this.fetchUsages()
  },

  beforeDestroy() {
    Object.values(this.charts).forEach(c => c && c.destroy())
  },
}
</script>

<style scoped>
.fs-13 { font-size: 13px; }
.fs-14 { font-size: 14px; }
.fs-15 { font-size: 15px; }
.fs-17 { font-size: 17px; }
.fs-18 { font-size: 18px; }

.chart-wrap {
  position: relative;
  height: 320px;
}
.chart-wrap canvas {
  max-height: 100%;
}
</style>